// Plans: 7-day free trial, then monthly or yearly PayPal subscription. Prices come from PayPal via the server.
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useSession } from "../lib/session";
import { useToast } from "../lib/toast";
import type { BillingConfig, PlanInfo } from "../lib/types";
import { SignInPanel } from "../components/SignInPanel";
import { pageTitle } from "../brand";
import { POLICY_AGREEMENT, POLICY_HEADINGS, POLICY_POINTS, POLICY_VERSION } from "../../shared/policy";

declare global {
  interface Window {
    paypal?: {
      Buttons: (opts: {
        style?: Record<string, unknown>;
        createSubscription: () => Promise<string>;
        onApprove: (data: { subscriptionID?: string | null }) => Promise<void>;
        onError?: (err: unknown) => void;
      }) => { render: (el: HTMLElement) => Promise<void>; close?: () => void };
    };
  }
}

let sdk: Promise<void> | null = null;
function loadSdk(clientId: string) {
  if (window.paypal) return Promise.resolve();
  sdk ??= new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&vault=true&intent=subscription&components=buttons`;
    s.onload = () => resolve();
    s.onerror = () => { sdk = null; reject(new Error("PayPal couldn't load. Check your connection or ad blocker.")); };
    document.head.appendChild(s);
  });
  return sdk;
}

const fmtDate = (s: string | null | undefined) => (s ? new Date(s).toLocaleDateString(undefined, { dateStyle: "long" }) : "");
const money = (p: PlanInfo) => `${p.currency === "USD" ? "US$" : p.currency + " "}${Number(p.price) % 1 === 0 ? Number(p.price).toFixed(0) : p.price}`;

function SubscribeButton({ plan, clientId, onApproved }: { plan: PlanInfo; clientId: string; onApproved: (id: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let b: { close?: () => void } | null = null;
    loadSdk(clientId).then(() => {
      if (!ref.current || !window.paypal) return;
      ref.current.innerHTML = "";
      b = window.paypal.Buttons({
        style: { layout: "vertical", label: "subscribe", height: 42, tagline: false },
        createSubscription: async () => (await api.subscribe(plan.key, POLICY_VERSION)).subscriptionId,
        onApprove: async (data) => { if (data.subscriptionID) onApproved(data.subscriptionID); },
        onError: (e) => setErr(e instanceof ApiError ? e.message : "PayPal reported a problem. You have not been charged."),
      });
      return (b as { render: (el: HTMLElement) => Promise<void> }).render(ref.current);
    }).catch((e: Error) => setErr(e.message));
    return () => b?.close?.();
  }, [plan.key, clientId, onApproved]);
  return <>{<div ref={ref} className="pp-slot" />}{err && <p className="form-error">{err}</p>}</>;
}

export default function Pricing() {
  const { me, refresh } = useSession();
  const toast = useToast();
  const [cfg, setCfg] = useState<BillingConfig | null>(null);
  const [processing, setProcessing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [agreed, setAgreed] = useState(false);
  useEffect(() => {
    document.title = pageTitle("Plans");
    api.billingConfig().then(setCfg).catch(() => setCfg({ enabled: false, clientId: null, env: "", plans: [], trialDays: 7 }));
  }, []);

  // After PayPal approval the SERVER verifies with PayPal; access starts only once the payment is confirmed.
  const onApproved = useCallback(async (subscriptionId: string) => {
    setProcessing(true);
    try {
      let r = await api.activate(subscriptionId);
      for (let i = 0; r.pending && i < 15; i++) {
        await new Promise((res) => setTimeout(res, 2000));
        r = await api.activate(subscriptionId);
      }
      await refresh();
      toast(r.pending ? "PayPal is still confirming your payment. Access starts as soon as it clears." : "You're subscribed. Thank you!");
    } catch (e) {
      toast((e as ApiError).message, "error");
    } finally {
      setProcessing(false);
    }
  }, [refresh, toast]);

  const cancel = async () => {
    if (!confirm("Cancel your subscription?\n\nIt won't renew, and you keep access until the end of the period you've already paid for.\n\nUnder our No Refund Policy there are NO refunds and NO pro-rata refunds for the remaining time.")) return;
    setCancelling(true);
    try {
      await api.cancelSubscription();
      await refresh();
      toast("Subscription cancelled. It won't renew. No pro-rata refund applies.");
    } catch (e) {
      toast((e as ApiError).message, "error");
    } finally {
      setCancelling(false);
    }
  };

  const a = me?.access;
  const monthly = cfg?.plans.find((p) => p.key === "monthly");
  const yearly = cfg?.plans.find((p) => p.key === "yearly");
  const freeMonths = monthly && yearly ? Math.round((12 * Number(monthly.price) - Number(yearly.price)) / Number(monthly.price)) : 0;
  const subscribed = a?.state === "subscribed";
  const renewing = subscribed && a?.subscription?.renews;
  const daysLeft = a?.trialEndsAt ? Math.max(0, Math.ceil((Date.parse(a.trialEndsAt) - Date.now()) / 86400000)) : 0;

  return (
    <div className="page reading pricing">
      <h1>Plans</h1>
      <p className="lead">Every plan includes all 50 lessons, the AI Adaptation Studio, Materials Kits, Teach mode, class profiles and email delivery.</p>

      {me?.user && a && (
        <div className={`status status-${a.state}`}>
          {a.state === "trial" && <p><b>Free trial: {daysLeft} day{daysLeft === 1 ? "" : "s"} left</b> (ends {fmtDate(a.trialEndsAt)}). Subscribing now starts billing today.</p>}
          {subscribed && (
            <p>
              <b>You're subscribed ({a.subscription?.plan === "yearly" ? "yearly" : "monthly"}).</b>{" "}
              {renewing ? <>Renews on {fmtDate(a.subscription?.periodEnd)}.</> : <>Cancelled. Access continues until {fmtDate(a.subscription?.periodEnd)}. No pro-rata refund applies.</>}
            </p>
          )}
          {a.state === "complimentary" && <p><b>You have complimentary access.</b></p>}
          {a.state === "none" && (
            <p><b>{a.trialDeniedReason === "email" ? "This email address has already had a free trial." : a.trialDeniedReason === "ip" ? "A free trial has already been used from this network." : a.trialDeniedReason === "disposable" ? "Free trials aren't available for temporary email addresses." : a.trialEndsAt ? "Your free trial has ended." : "No active plan."}</b> Choose a plan to continue.</p>
          )}
          {renewing && <button className="btn btn-quiet" onClick={() => void cancel()} disabled={cancelling}>{cancelling ? "Cancelling…" : "Cancel subscription"}</button>}
        </div>
      )}

      {processing && <div className="notice"><p className="notice-title">Confirming your payment with PayPal…</p><p>This usually takes a few seconds. Please keep this page open.</p></div>}

      <section className="policy" id="refund-policy" aria-labelledby="policy-h">
        <h2 id="policy-h" className="policy-h">{POLICY_HEADINGS[0]}</h2>
        <p className="policy-h2">{POLICY_HEADINGS[1]}</p>
        <ul>{POLICY_POINTS.map((t) => <li key={t}>{t}</li>)}</ul>
      </section>

      <div className="plan-grid">
        <div className="plan plan-trial">
          <p className="plan-name">Free trial</p>
          <p className="plan-price">{cfg?.trialDays ?? 7} days</p>
          <p className="muted small">No card needed. One trial per person and per network.</p>
          {!me?.user && <SignInPanel reason="" />}
        </div>
        {[monthly, yearly].filter(Boolean).map((p) => (
          <div key={p!.key} className={`plan${p!.key === "yearly" ? " plan-best" : ""}`}>
            <p className="plan-name">{p!.key === "yearly" ? "Yearly" : "Monthly"} {p!.key === "yearly" && freeMonths > 0 && <span className="save-badge">{freeMonths} months free</span>}</p>
            <p className="plan-price">{money(p!)}<span className="muted small"> / {p!.interval === "YEAR" ? "year" : "month"}</span></p>
            <p className="muted small">Renews automatically. Cancel any time.</p>
            {me?.user && cfg?.clientId && !renewing && !processing && (agreed
              ? <SubscribeButton plan={p!} clientId={cfg.clientId} onApproved={onApproved} />
              : <p className="small policy-hint">Tick the No Refund Policy box below to subscribe.</p>)}
            {!me?.user && <p className="small">Sign in first to subscribe.</p>}
          </div>
        ))}
        {cfg && !cfg.enabled && (
          <div className="plan"><p className="plan-name">Monthly · Yearly</p><p className="plan-price">US$10 · US$100</p><p className="muted small">Subscriptions open soon.</p></div>
        )}
      </div>
      {me?.user && cfg?.enabled && !renewing && (
        <label className="check policy-agree">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span>{POLICY_AGREEMENT}</span>
        </label>
      )}
      <p className="muted small">Payments are processed by PayPal{cfg?.env && cfg.env !== "live" ? " (sandbox test mode)" : ""}. We never see your card details. If a payment is reversed or disputed, access ends immediately.</p>
    </div>
  );
}
